import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { EnvelopeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { Button } from '~/components/buttons/Button/Button';
import {
  MODAL_BACKDROP_CLASS,
  MODAL_CLOSE_BTN_CLASS,
  MODAL_INPUT_CLASS,
  MODAL_LABEL_CLASS,
  MODAL_LINK_BTN_CLASS,
  MODAL_PANEL_CLASS,
} from './modalStyles';
import type { ManagedInviteError } from './providerAssociation.machine';

const TAGS = CT.employer.providerAssociations.managedInviteModal;

interface ManagedInviteModalProps {
  open: boolean;
  sending: boolean;
  providerName: string;
  email: string;
  error: ManagedInviteError | null;
  onClose: () => void;
  onEmailChange: (email: string) => void;
  onSend: () => void;
  onSendNormalInvite: () => void;
}

// [MCV-1894] "Send invite" on an employer-managed row: asks for the email the
// provider will sign up with, then invites them to claim the vault.
export const ManagedInviteModal = ({
  open,
  sending,
  providerName,
  email,
  error,
  onClose,
  onEmailChange,
  onSend,
  onSendNormalInvite,
}: ManagedInviteModalProps) => {
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop transition className={MODAL_BACKDROP_CLASS} />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel transition data-cy={TAGS.modal} className={MODAL_PANEL_CLASS}>
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <EnvelopeIcon className="h-5 w-5 text-content-secondary" />
                <DialogTitle className="text-lg font-semibold text-grey-100">
                  Send Invite
                </DialogTitle>
              </div>
              <button onClick={onClose} className={MODAL_CLOSE_BTN_CLASS} type="button">
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-5 w-5 text-content-secondary" />
              </button>
            </div>

            <p className="mb-4 text-sm text-content-secondary">
              {`Enter the email ${providerName} will sign up with. They will be invited to claim the vault you set up.`}
            </p>

            <label htmlFor="managed-invite-email" className={MODAL_LABEL_CLASS}>
              Email Address
            </label>
            <input
              id="managed-invite-email"
              data-cy={TAGS.emailInput}
              type="email"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onSend()}
              placeholder="provider@example.com"
              className={MODAL_INPUT_CLASS}
              disabled={sending}
            />

            {error === 'INVALID_EMAIL' && (
              <p data-cy={TAGS.errorMsg} className="mb-3 text-sm text-error">
                Please enter a valid email address
              </p>
            )}
            {error === 'PENDING_REQUEST_EXISTS' && (
              <p data-cy={TAGS.errorMsg} className="mb-3 text-sm text-error">
                {`A pending request already exists for ${email.trim()}. Resend it from the Invitations tab.`}
              </p>
            )}
            {error === 'EMAIL_IN_USE' && (
              <p data-cy={TAGS.errorMsg} className="mb-3 text-sm text-error">
                {`${email.trim()} is already used by another provider. Use a different email.`}
              </p>
            )}
            {error === 'PROVIDER_HAS_ACCOUNT' && (
              <p data-cy={TAGS.hasAccountMsg} className="mb-3 text-sm text-error">
                This provider already has an account — send a normal invite.{' '}
                <button
                  type="button"
                  data-cy={TAGS.normalInviteBtn}
                  className={MODAL_LINK_BTN_CLASS}
                  onClick={onSendNormalInvite}
                >
                  Send a normal invite
                </button>
              </p>
            )}

            <div className="mt-4 flex justify-end gap-3">
              <Button
                cyTag={TAGS.cancelBtn}
                type="button"
                colorVariant="secondary"
                onClick={onClose}
                disabled={sending}
              >
                Cancel
              </Button>
              <Button
                cyTag={TAGS.sendBtn}
                type="button"
                colorVariant="primary"
                onClick={onSend}
                disabled={sending}
              >
                {sending ? 'Sending...' : 'Send Invitation'}
              </Button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
